import React, { Component } from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router";
import querystring from "query-string";

import LoadingOverlay from "./LoadingOverlay";
import { handleError } from "@/util/errorHandler";
import {
  renderTotal,
  renderPaginationPanel,
  renderNoData,
} from "@/util/paginationHelper";
import { defaults } from "lodash";
import ExportReportButton from "@/views/Components/ExportReport/ExportReportButton";
import { debounce } from "lodash";
import { APP_CONST } from "@/store/dataStore";

class TableContainerWithLocation extends Component {
  state = {
    data: [],
    total: 0,
    hasNext: false,
    hasPrevious: false,
    isLoading: false,
    filter: "",
  };

  /**
   * Get search parameters for making an API request
   *
   * @returns {Object}
   */
  getParams = () => {
    const params = querystring.parse(this.props.location.search);
    const defaultParams = {
      page: 0,
      size: 10,
      filter: this.state.filter,
    };
    params.size = params.sizePerPage;
    delete params.sizePerPage;
    const pm = defaults(
      params,
      defaultParams,
      this.props.reportAdditionalParams,
    );
    return pm;
  };

  createExportReportButton = () => {
    return (
      <ExportReportButton
        fromAccountDetails={this.props.fromAccountDetails}
        params={this.getParams()}
        requestReport={this.props.requestReport}
        disableDateRangePicker={this.props.reportWithoutDateRange}
        initialDates={this.props.reportWithInitialDates}
        disableSelectedBeforeCurrentDate={
          this.props.isTransaction ? APP_CONST.REPORT_BACK_DAYS() : 0
        }
      />
    );
  };

  loadAndStoreData = async () => {
    const {
      filter,
      sort = this.props.isTransaction ? "transactionCreatedTime,desc" : "",
      page,
      size: sizePerPage,
      businessStatus,
      status,
    } = this.getParams();

    this.setState({ isLoading: true });

    try {
      const { data, total, hasNext, hasPrevious } = await this.props.loadData(
        page,
        sizePerPage,
        filter,
        sort,
        businessStatus,
        status,
      );
      this.setState({ data, total, hasNext, hasPrevious });
    } catch (e) {
      handleError(e);
    } finally {
      this.setState({ isLoading: false });
    }
  };

  handlePageChange = page => {
    if (Number.isInteger(page)) {
      this.handleSearchQueryChange({ page: page - 1 });
    }
  };

  handleUpdateCurrentPage = () => {
    this.loadAndStoreData();
  };

  handleSizePerPageChange = sizePerPage => {
    this.handleSearchQueryChange({ sizePerPage, page: 0 });
    if (this.props.onSearchClicked || this.props.onSearchQueryChanged) {
      setTimeout(() => {
        this.loadAndStoreData();
      }, 300);
    }
  };

  handleFilterValueChange = filter => {
    this.setState({
      filter: filter,
    });
  };

  handleFilterChange = filter => {
    if (this.props.onSearchClicked) {
      const filter = this.state.filter;
      this.props.onSearchClicked({
        filter,
        page: 0,
      });
    } else {
      this.handleSearchQueryChange({ filter, page: 0 });
      if (this.props.onSearchQueryChanged) {
        this.props.onSearchQueryChanged();
      }
    }
  };

  handleSortChange = (sortName, sortOrder) => {
    const filter = this.state.filter;
    const sort = `${sortName},${sortOrder}`;
    if (this.props.onSearchClicked) {
      this.props.onSearchClicked({
        filter,
        page: 0,
        sort,
      });
    } else {
      this.handleSearchQueryChange({ sort });
      if (this.props.onSearchQueryChanged) {
        this.props.onSearchQueryChanged();
      }
    }
  };

  handleBusinessStatusChange = businessStatus => {
    this.handleSearchQueryChange({ businessStatus, page: 0 });
    if (this.props.onSearchQueryChanged) {
      this.props.onSearchQueryChanged();
    }
  };

  handleStatusChange = status => {
    this.handleSearchQueryChange({ status, page: 0 });
    if (this.props.onSearchQueryChanged) {
      this.props.onSearchQueryChanged();
    }
  };

  componentDidMount() {
    if (this.props.autoLoad) {
      this.loadAndStoreData();
    }
  }

  componentDidUpdate(prevProps) {
    if (
      prevProps.location.search !== this.props.location.search &&
      !this.props.onSearchClicked &&
      !this.props.onSearchQueryChanged
    ) {
      this.loadAndStoreData();
    }
  }

  handleSearchQueryChange = newQueryParams => {
    const { history, location } = this.props;

    const oldSearchQuery = querystring.parse(location.search);
    const newSearchQuery = querystring.stringify({
      ...oldSearchQuery,
      ...newQueryParams,
    });

    history.push({
      ...location,
      search: newSearchQuery,
    });
  };

  render() {
    const { page: pageString, size: sizePerPageString } = this.getParams();

    const page = Number(pageString);
    const sizePerPage = Number(sizePerPageString);
    const options = {
      sizePerPageList: [
        {
          text: "5",
          value: 5,
        },
        {
          text: "10",
          value: 10,
        },
        {
          text: "25",
          value: 25,
        },
        {
          text: "50",
          value: 50,
        },
      ],
      onPageChange: this.handlePageChange,
      onSizePerPageList: this.handleSizePerPageChange,
      onSearchChange:
        this.props.isTransaction === true
          ? this.handleFilterValueChange
          : debounce(this.handleFilterChange, 1000),
      onSearchClicked: this.handleFilterChange,
      onSortChange: this.handleSortChange,
      page: page + 1,
      sizePerPage,
      hidePageListOnlyOnePage: true,
      paginationShowsTotal: renderTotal,
      paginationPanel: renderPaginationPanel,
      noDataText: renderNoData(this.state.isLoading),
      exportCSVBtn: this.createExportReportButton,
    };

    const fetchInfo = {
      dataTotalSize: this.state.total,
      hasNext: this.state.hasNext,
      hasPrevious: this.state.hasPrevious,
    };

    // TODO: Maybe move this loader into the table, as it is done here:
    // https://stackoverflow.com/questions/43338884/how-add-react-loading-in-react-bootstrap-table
    return (
      <div style={{ position: "relative" }}>
        <LoadingOverlay loading={this.state.isLoading} />
        {React.cloneElement(this.props.children, {
          options: { ...options, ...this.props.options },
          data: this.state.data,
          fetchInfo: fetchInfo,
          onBusinessStatusChange: this.handleBusinessStatusChange,
          onStatusChange: this.handleStatusChange,
        })}
      </div>
    );
  }
}

TableContainerWithLocation.defaultProps = {
  autoLoad: true,
  options: {},
  reportAdditionalParams: {},
  isTransaction: false,
  fromAccountDetails: false,
};

TableContainerWithLocation.propTypes = {
  loadData: PropTypes.func,
  requestReport: PropTypes.func,
  onSearchClicked: PropTypes.func,
  onSearchQueryChanged: PropTypes.func,
  reportWithoutDateRange: PropTypes.bool,
  reportAdditionalParams: PropTypes.object,
  children: PropTypes.element.isRequired,
  autoLoad: PropTypes.bool,
  location: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  reportWithInitialDates: ExportReportButton.propTypes.initialDates,
  isTransaction: PropTypes.bool,
  fromAccountDetails: PropTypes.bool,
};

export default withRouter(TableContainerWithLocation);
